import { useCurrentUser, useRouteAllowed } from '@billx/api';
import type { AccessRoleType } from '@billx/types';
import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteGuardProps {
  children: React.ReactNode;
  requiredRoles?: AccessRoleType[];
  pageKey?: string;
  fallbackPath?: string;
}

export const ProtectedRouteGuard: React.FC<ProtectedRouteGuardProps> = ({
  children,
  pageKey,
  fallbackPath = '/login',
}) => {
  const { currentUser } = useCurrentUser();
  const { allowed, isLoading } = useRouteAllowed(pageKey ?? '');
  const checkAccess = !!pageKey;

  if (!currentUser) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (checkAccess && isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (checkAccess && !allowed) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          You don&apos;t have permission to access this page.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Page: {pageKey}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
